<template>
  <v-container fluid class="home-container py-8 px-4 px-md-8">
    <!-- Hero Header -->
    <v-row justify="center" class="mb-0">
      <v-col cols="12" md="10" class="text-center pt-12">
        <!-- Prestige Logo/Award Badge -->
        <div v-if="false" class="awards-badge mb-4 animate-fade-in">
          <v-icon color="#00D4FF" size="24" class="mr-2">mdi-trophy-outline</v-icon>
          <span class="badge-text">OFFICIAL CAMPAIGN 2026</span>
        </div>
        
        <h1 class="awards-main-heading font-weight-black mb-10 animate-slide-up">
          <span class="gradient-text">SUNWAY MULTICARE PHARMACY TOP CHOICE AWARDS 2026</span>
        </h1>

        
        <!-- Hero Banner Frame -->
        <div class="hero-image-frame hover-lift mb-8 animate-fade-in-delayed">
          <v-img
            src="/awards_hero_banner.png"
            class="hero-banner-image"
            alt="MultiCare Awards Banner"
            max-height="450"
            contain
            eager
          ></v-img>
        </div>
        
        <!-- Action Button -->
        <v-btn
          class="animated-gold-btn px-12 py-7 mb-1 animate-pulse-glow"
          large
          to="/vote"
        >
          <v-icon left size="24" color="white" class="mr-2">mdi-vote-outline</v-icon>
          <span class="btn-text">VOTE NOW</span>
        </v-btn>
      </v-col>
    </v-row>

    <!-- Stats & Countdown Grid -->
    <v-row justify="center" class="mb-12">
      <v-col cols="12" md="10">
        <v-card class="glassy-card pa-6 text-center shadow-premium">
          <v-row align="center">
            <!-- Countdown Section -->
            <v-col cols="12" md="6" class="border-right-md py-4">
              <h3 class="gold-subtitle mb-4">VOTING ENDS IN</h3>
              <div class="countdown-display d-flex justify-center align-center">
                <div class="countdown-item">
                  <div class="countdown-number">{{ countdown.days }}</div>
                  <div class="countdown-label">Days</div>
                </div>
                <div class="countdown-colon">:</div>
                <div class="countdown-item">
                  <div class="countdown-number">{{ countdown.hours }}</div>
                  <div class="countdown-label">Hours</div>
                </div>
                <div class="countdown-colon">:</div>
                <div class="countdown-item">
                  <div class="countdown-number">{{ countdown.minutes }}</div>
                  <div class="countdown-label">Mins</div>
                </div>
                <div class="countdown-colon">:</div>
                <div class="countdown-item">
                  <div class="countdown-number">{{ countdown.seconds }}</div>
                  <div class="countdown-label">Secs</div>
                </div>
              </div>
            </v-col>
            
            <!-- Live Votes Section -->
            <v-col cols="12" md="6" class="py-4">
              <h3 class="gold-subtitle mb-4">LIVE PARTICIPATION</h3>
              <div class="voter-counter mb-2">
                <v-icon color="#00D4FF" class="live-pulse mr-2" size="18">mdi-record</v-icon>
                <span class="counter-number">{{ animatedVoteCount.toLocaleString() }}</span>
              </div>
              <p class="counter-description mb-0">Total entries submitted in this campaign</p>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>

    <!-- How To Play -->
    <v-row justify="center" class="mb-12">
      <v-col cols="12" md="10">
        <h2 class="section-title text-center mb-8">
          <v-icon color="#00D4FF" class="mr-2">mdi-help-circle-outline</v-icon>
          HOW TO PARTICIPATE
        </h2>
        <v-row justify="center">
          <v-col cols="12" sm="6" md="6" v-for="(step, i) in steps" :key="i">
            <v-card class="step-card pa-6 text-center hover-lift h-100">
              <div class="step-icon-badge mb-4">
                <span class="step-index">{{ i + 1 }}</span>
                <v-icon :color="step.color" size="36">{{ step.icon }}</v-icon>
              </div>
              <h3 class="step-title mb-2">{{ step.title }}</h3>
              <p class="step-desc mb-0">{{ step.description }}</p>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
    </v-row>



    <!-- Voting Timeline -->
    <v-row justify="center" class="mb-8">
      <v-col cols="12" md="8">
        <v-card class="glassy-card pa-6 shadow-premium">
          <h2 class="section-title text-left mb-6">
            <v-icon color="#00D4FF" class="mr-2">mdi-calendar-clock</v-icon>
            Campaign Schedule
          </h2>
          <v-timeline dense>
            <v-timeline-item
              v-for="(phase, i) in timeline"
              :key="i"
              :color="phase.color"
              fill-dot
              small
            >
              <div class="text-left py-1">
                <div class="timeline-title font-weight-bold mb-1">{{ phase.title }}</div>
                <div class="timeline-date font-weight-medium">{{ phase.date }}</div>
              </div>
            </v-timeline-item>
          </v-timeline>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import config from '@/config.js';

export default {
  name: 'HomeView',
  data() {
    // Calculate base vote count: starts at 12000 on 2026-08-01, adds 15-43 daily deterministically
    const calculateBase = () => {
      const baseValue = 12000;
      const startDate = new Date('2026-08-01');
      const now = new Date();
      const startMidnight = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
      const nowMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const diffTime = Math.max(0, nowMidnight - startMidnight);
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      
      let totalIncrement = 0;
      for (let i = 0; i < diffDays; i++) {
        // Deterministic pseudo-random increment between 15 and 43 for each day
        const x = Math.sin(i + 1.5) * 10000;
        const daily = 15 + Math.floor((x - Math.floor(x)) * (43 - 15 + 1));
        totalIncrement += daily;
      }
      return baseValue + totalIncrement;
    };
    
    const computedBase = calculateBase();

    return {
      countdownDate: new Date('2026-09-12T23:59:59+08:00'), // Campaign Closing Date
      countdown: {
        days: '00',
        hours: '00',
        minutes: '00',
        seconds: '00'
      },
      actualVoteCount: 0,
      baseVoteCount: computedBase,
      animatedVoteCount: computedBase,
      countdownTimer: null,
      counterTimer: null,
      steps: [
        {
          icon: 'mdi-ballot-outline',
          title: 'Cast Your Vote',
          description: 'Explore health & beauty categories. Select one brand from each subcategory.',
          color: '#00D4FF',
        },
        {
          icon: 'mdi-account-edit-outline',
          title: 'Register Details',
          description: 'Provide your name, phone number, and gender to finalize your submission.',
          color: '#2C2C34',
        },
      ],
      timeline: [
        {
          title: 'Voting Period Opens',
          date: '12th August 2026',
          color: '#00D4FF',
        },
        {
          title: 'Voting Period Closes',
          date: '12th September 2026 (11:59 PM)',
          color: '#00D4FF',
        },
        {
          title: 'Grand Winners Announcement',
          date: '1st October 2026',
          color: '#00D4FF',
        },
      ],
    };
  },
  mounted() {
    this.startCountdown();
    this.fetchVoteStats();
    
    // Simulate live ticking voter counter
    this.counterTimer = setInterval(() => {
      if (this.animatedVoteCount < this.baseVoteCount + this.actualVoteCount) {
        this.animatedVoteCount += Math.floor(Math.random() * 2) + 1;
      } else {
        // Slow tick for dynamic activity
        if (Math.random() > 0.4) {
          this.animatedVoteCount += 1;
        }
      }
    }, 4000);
  },
  beforeDestroy() {
    if (this.countdownTimer) clearInterval(this.countdownTimer);
    if (this.counterTimer) clearInterval(this.counterTimer);
  },
  methods: {
    startCountdown() {
      const updateTimer = () => {
        const now = new Date().getTime();
        const difference = this.countdownDate.getTime() - now;
        
        if (difference <= 0) {
          clearInterval(this.countdownTimer);
          return;
        }
        
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);
        
        this.countdown.days = days.toString().padStart(2, '0');
        this.countdown.hours = hours.toString().padStart(2, '0');
        this.countdown.minutes = minutes.toString().padStart(2, '0');
        this.countdown.seconds = seconds.toString().padStart(2, '0');
      };
      
      updateTimer();
      this.countdownTimer = setInterval(updateTimer, 1000);
    },
    async fetchVoteStats() {
      try {
        const response = await fetch(`${config.API_BASE_URL}/votes`);
        if (response.ok) {
          const data = await response.json();
          this.actualVoteCount = Array.isArray(data) ? data.length : 0;
          this.animatedVoteCount = this.baseVoteCount + this.actualVoteCount;
        }
      } catch (err) {
        console.warn('Failed to fetch actual vote count, using base simulation.', err);
      }
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;600;700;800;900&display=swap');

.home-container {
  max-width: 1200px;
  margin: 0 auto;
  font-family: 'Outfit', 'Poppins', sans-serif;
  padding-top: 48px !important;
}

/* Awards Badge */
.awards-badge {
  display: inline-flex;
  align-items: center;
  border: 1.5px solid rgba(0, 212, 255, 0.4);
  padding: 8px 24px;
  border-radius: 30px;
  background: linear-gradient(135deg, #2C2C34 0%, #1A1A20 100%);
  box-shadow: 0 4px 15px rgba(0, 212, 255, 0.15);
  transition: all 0.3s ease;
}

.awards-badge:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(0, 212, 255, 0.25);
  border-color: rgba(0, 212, 255, 0.7);
}

.badge-text {
  font-size: 0.8rem;
  font-weight: 800;
  color: #00D4FF;
  letter-spacing: 3px;
  text-transform: uppercase;
}

/* Heading styles */
.awards-main-heading {
  font-size: clamp(2rem, 5vw, 3.8rem);
  line-height: 1.2;
  letter-spacing: 2px;
  word-spacing: 12px;
}

.gradient-text {
  color: #00D4FF !important;
  text-shadow: 0px 4px 10px rgba(20, 20, 26, 0.85), 0px 1px 3px rgba(20, 20, 26, 0.95);
}

.awards-subtitle {
  font-size: clamp(0.9rem, 2.5vw, 1.15rem);
  color: #2C2C34;
  letter-spacing: 2px;
}

/* Hero image frame */
.hero-image-frame {
  background: transparent;
  border: none;
  border-radius: 36px;
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
  padding: 0;
}

.hero-image-frame:hover {
  transform: translateY(-8px);
}

.hero-banner-image {
  border-radius: 36px !important;
}

/* Animated Gold Button */
.animated-gold-btn {
  background: linear-gradient(135deg, #2C2C34 0%, #00D4FF 50%, #2C2C34 100%) !important;
  background-size: 200% auto !important;
  border: 1.5px solid rgba(0, 212, 255, 0.4) !important;
  border-radius: 50px !important;
  color: white !important;
  box-shadow: 0 8px 24px rgba(0, 212, 255, 0.15), 0 0 15px rgba(0, 212, 255, 0.1) !important;
  transition: all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1) !important;
}

.animated-gold-btn:hover {
  background-position: right center !important;
  transform: scale(1.05) translateY(-3px);
  box-shadow: 0 15px 35px rgba(0, 212, 255, 0.45), 0 0 20px rgba(0, 212, 255, 0.2) !important;
  border-color: rgba(0, 212, 255, 0.8) !important;
}

.btn-text {
  font-size: 1.2rem;
  font-weight: 800;
  letter-spacing: 2px;
}

/* Glassmorphism card */
.glassy-card {
  background: linear-gradient(135deg, rgba(44, 44, 52, 0.95) 0%, rgba(20, 20, 26, 0.9) 100%) !important;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1.5px solid rgba(0, 212, 255, 0.25) !important;
  border-radius: 24px !important;
  transition: all 0.3s ease;
  box-shadow: 0 15px 35px rgba(0, 212, 255, 0.08) !important;
}

.border-right-md {
  border-right: 1.5px solid rgba(255, 255, 255, 0.1);
}

@media (max-width: 959px) {
  .border-right-md {
    border-right: none;
    border-bottom: 1.5px solid rgba(255, 255, 255, 0.1);
  }
}

.gold-subtitle {
  font-size: 0.85rem;
  font-weight: 800;
  letter-spacing: 3px;
  color: #00D4FF;
}

/* Countdown component */
.countdown-display {
  gap: 8px;
}

.countdown-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(0, 212, 255, 0.25);
  padding: 8px 10px;
  border-radius: 12px;
  min-width: 64px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
}

.countdown-number {
  font-size: 1.7rem;
  font-weight: 900;
  color: #00D4FF;
  line-height: 1;
  text-shadow: 0 0 10px rgba(0, 212, 255, 0.2);
}

.countdown-label {
  font-size: 0.6rem;
  font-weight: 800;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.5);
  margin-top: 4px;
  letter-spacing: 0.5px;
}

.countdown-colon {
  font-size: 1.7rem;
  font-weight: 900;
  color: rgba(0, 212, 255, 0.7);
  line-height: 1;
  padding-bottom: 16px;
}

/* Live counter */
.voter-counter {
  display: flex;
  align-items: center;
  justify-content: center;
}

.counter-number {
  font-size: 2.3rem;
  font-weight: 900;
  color: #00D4FF;
  letter-spacing: -0.5px;
  text-shadow: 0 0 15px rgba(0, 212, 255, 0.35);
}

.live-pulse {
  animation: pulse-cyan 1.5s infinite;
}

.counter-description {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.6);
  font-weight: 500;
}

/* Section Title */
.section-title {
  font-size: clamp(1.4rem, 4vw, 1.8rem);
  font-weight: 800;
  color: #00D4FF;
  text-shadow: 0px 2px 8px rgba(20, 20, 26, 0.8), 0px 1px 2px rgba(20, 20, 26, 0.9);
  letter-spacing: -0.5px;
}

/* Step cards */
.step-card {
  background: rgba(255, 255, 255, 0.9) !important;
  border: 1px solid rgba(0, 212, 255, 0.1) !important;
  border-radius: 20px !important;
  transition: all 0.3s cubic-bezier(0.165, 0.84, 0.44, 1);
  box-shadow: 0 4px 20px rgba(44, 44, 52, 0.05) !important;
}

.step-icon-badge {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: rgba(0, 212, 255, 0.08);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  margin: 0 auto;
}

.step-index {
  position: absolute;
  top: -4px;
  right: -4px;
  background: #00D4FF;
  color: white;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  font-size: 0.75rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid white;
}

.step-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #2C2C34;
}

.step-desc {
  font-size: 0.95rem;
  color: #546E7A;
  line-height: 1.5;
}

/* Prize cards */
.prize-card {
  background: rgba(255, 255, 255, 0.95) !important;
  border: 1px solid rgba(0, 212, 255, 0.25) !important;
  border-radius: 20px !important;
  box-shadow: 0 6px 22px rgba(0, 212, 255, 0.05) !important;
}

.prize-badge {
  display: inline-flex;
  align-items: center;
  background: rgba(0, 212, 255, 0.1);
  border: 1px solid rgba(0, 212, 255, 0.3);
  padding: 4px 10px;
  border-radius: 8px;
  font-size: 0.7rem;
  font-weight: 800;
  color: #00D4FF;
  letter-spacing: 1px;
}

.prize-name {
  font-size: 1.2rem;
  font-weight: 800;
  color: #2C2C34;
}

.prize-desc {
  font-size: 0.85rem;
  color: #546E7A;
  line-height: 1.4;
}

/* Timeline updates */
.timeline-title {
  font-size: 1rem;
  color: #FFFFFF !important;
}

.timeline-date {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.7) !important;
}

.glassy-card .section-title {
  color: #FFFFFF !important;
}

/* Animation utilities */
@keyframes pulse-cyan {
  0% { transform: scale(0.95); opacity: 0.8; box-shadow: 0 0 0 0 rgba(0, 212, 255, 0.4); }
  50% { transform: scale(1.15); opacity: 1; box-shadow: 0 0 0 8px rgba(0, 212, 255, 0); }
  100% { transform: scale(0.95); opacity: 0.8; box-shadow: 0 0 0 0 rgba(0, 212, 255, 0); }
}

.animate-pulse-glow {
  animation: pulse-glow-anim 2.5s infinite ease-in-out;
}

@keyframes pulse-glow-anim {
  0% { transform: scale(1); box-shadow: 0 8px 24px rgba(0, 212, 255, 0.25); }
  50% { transform: scale(1.03); box-shadow: 0 12px 30px rgba(0, 212, 255, 0.45), 0 0 15px rgba(0, 212, 255, 0.2); }
  100% { transform: scale(1); box-shadow: 0 8px 24px rgba(0, 212, 255, 0.25); }
}

.hover-lift {
  transition: all 0.3s cubic-bezier(0.165, 0.84, 0.44, 1);
}

.hover-lift:hover {
  transform: translateY(-6px);
  box-shadow: 0 15px 35px rgba(0, 212, 255, 0.12) !important;
}

.animate-fade-in {
  animation: fadeIn 0.8s ease-out;
}

.animate-fade-in-delayed {
  animation: fadeIn 1.2s ease-out;
}

.animate-slide-up {
  animation: slideUp 0.8s cubic-bezier(0.165, 0.84, 0.44, 1);
}

.animate-slide-up-delayed {
  animation: slideUp 1s cubic-bezier(0.165, 0.84, 0.44, 1);
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideUp {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}
</style>
